import React, { useState, useEffect, useRef } from 'react';
import { Field, Input, Label, Textarea } from '@headlessui/react';
import clsx from 'clsx';
import Modal from '@/components/ui/Modal';
import { OneTimerTask, UpdateOneTimerTaskRequest } from '../types';
import { useUpdateTask } from '../api/oneTimersApi';
import Button from '@/components/ui/Button';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  task: OneTimerTask;
}

const EditTaskModal: React.FC<Props> = ({ isOpen, onClose, task }) => {
  // Only state we need is for error handling
  const [error, setError] = useState<string | null>(null);

  // Refs for immediate input values (prevents re-renders)
  const titleRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const deadlineRef = useRef<HTMLInputElement>(null);

  const updateTaskMutation = useUpdateTask();

  // Sync form values when task changes or modal opens
  useEffect(() => {
    if (isOpen) {
      setError(null);
      
      // Update refs with task values
      if (titleRef.current) titleRef.current.value = task.title;
      if (descriptionRef.current) descriptionRef.current.value = task.description || '';
      if (deadlineRef.current) deadlineRef.current.value = task.deadline || '';
    }
  }, [isOpen, task.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    const currentTitle = titleRef.current?.value || '';
    const currentDescription = descriptionRef.current?.value || '';
    const currentDeadline = deadlineRef.current?.value || '';

    if (!currentTitle.trim()) {
      setError('Task title is required');
      return;
    }

    try {
      const updates: UpdateOneTimerTaskRequest = {
        title: currentTitle.trim(),
        description: currentDescription.trim(),
        deadline: currentDeadline || undefined,
      };

      await updateTaskMutation.mutateAsync({ id: task.id, updates });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Edit Task" maxWidth="md" bottomSheetMobile>
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Title Field */}
        <Field>
          <Label className="text-sm font-medium text-gray-700">Task Title *</Label>
          <Input
            ref={titleRef}
            type="text"
            defaultValue={task.title}
            className={clsx(
              'mt-1 block w-full rounded-md px-3 py-2 text-gray-900',
              'border shadow-sm transition-colors',
              'focus:outline-none focus:ring-2',
              error
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/20',
              'disabled:bg-gray-50 disabled:text-gray-500'
            )}
            disabled={updateTaskMutation.isPending}
          />
          {error && (
            <p className="mt-1 text-sm text-red-600">{error}</p>
          )}
        </Field>

        {/* Description Field */}
        <Field>
          <Label className="text-sm font-medium text-gray-700">Description</Label>
          <Textarea
            ref={descriptionRef}
            defaultValue={task.description || ''}
            rows={3}
            className={clsx(
              'mt-1 block w-full rounded-md px-3 py-2 text-gray-900',
              'border border-gray-300 shadow-sm',
              'focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-500/20',
              'disabled:bg-gray-50 disabled:text-gray-500',
              'resize-none'
            )}
            placeholder="Optional details..."
            disabled={updateTaskMutation.isPending}
          />
        </Field>

        {/* Deadline Field */}
        <Field>
          <Label className="text-sm font-medium text-gray-700">Deadline</Label>
          <Input
            ref={deadlineRef}
            type="date"
            defaultValue={task.deadline || ''}
            className={clsx(
              'mt-1 block w-full rounded-md px-3 py-2 text-gray-900',
              'border border-gray-300 shadow-sm',
              'focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-500/20',
              'disabled:bg-gray-50 disabled:text-gray-500'
            )}
            disabled={updateTaskMutation.isPending}
          />
        </Field>

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <Button variant="outline" className="flex-1" onClick={handleClose} disabled={updateTaskMutation.isPending}>
            Cancel
          </Button>
          <Button className="flex-1" type="submit" disabled={updateTaskMutation.isPending}>
            {updateTaskMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default EditTaskModal; 